import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Magnet,
  Download,
  Copy,
  Users,
  Activity,
  HardDrive,
  Calendar,
  Star,
  User,
  Tag,
  ImageOff,
} from "lucide-react";
import { fetchDetails, torrentFileUrl } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { relativeDate } from "@/lib/format";

function Stat({ icon: Icon, label, value, className = "" }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-neutral-500">
        <Icon size={11} /> {label}
      </span>
      <span className={`font-mono text-sm ${className}`}>{value ?? "—"}</span>
    </div>
  );
}

export function DetailsPanel({ torrent, hasDetails, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    setDetails(null);
    setImgOk(true);
    if (!torrent || !hasDetails) return;
    let cancelled = false;
    setLoading(true);
    fetchDetails({
      provider: torrent.providerName,
      url: torrent.descriptionPageUrl,
      infoHash: torrent.infoHash,
    })
      .then((d) => !cancelled && setDetails(d))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [torrent, hasDetails]);

  const t = torrent;
  if (!t) return null;

  const merged = {
    name: details?.name || t.name,
    size: details?.size || t.size,
    seeders: details?.seeders ?? t.seeders,
    peers: details?.peers ?? t.peers,
    category: details?.category || t.category,
    uploadDate: details?.uploadDate || t.uploadDate,
    magnetUri: details?.magnetUri || t.magnetUri,
    fileDownloadLink: details?.fileDownloadLink || t.fileDownloadLink,
    uploader: details?.uploader,
    description: details?.description,
    posterUrl: details?.posterUrl,
    screenshots: details?.screenshotUrls || [],
    rating: details?.rating,
    year: details?.year,
    genres: details?.genres || [],
  };

  const copyMagnet = () => {
    copyText(merged.magnetUri, "Magnet link copied");
  };

  return (
    <AnimatePresence>
      {t && (
        <>
          <motion.div
            data-testid="details-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="details-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[480px] bg-neutral-950 border-l border-neutral-800 overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 py-4 bg-neutral-950/90 backdrop-blur border-b border-neutral-800">
              <div className="min-w-0">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400">
                  {t.providerName}
                </span>
                <h2 className="text-sm font-semibold text-white leading-snug mt-1 break-words">
                  {merged.name}
                </h2>
              </div>
              <button
                data-testid="details-close"
                onClick={onClose}
                className="shrink-0 p-2 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Poster */}
              {merged.posterUrl && imgOk && (
                <div className="flex gap-4">
                  <img
                    data-testid="details-poster"
                    src={merged.posterUrl}
                    alt={merged.name}
                    onError={() => setImgOk(false)}
                    className="w-28 rounded-lg border border-neutral-800 object-cover"
                  />
                  <div className="flex flex-col gap-2 pt-1">
                    {merged.year && (
                      <span className="font-mono text-sm text-neutral-300">{merged.year}</span>
                    )}
                    {merged.rating != null && (
                      <span className="flex items-center gap-1.5 text-amber-400 font-mono text-sm">
                        <Star size={14} className="fill-amber-400" /> {merged.rating}/10
                      </span>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {merged.genres.map((g) => (
                        <span
                          key={g}
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400"
                        >
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  data-testid="details-copy-magnet"
                  onClick={copyMagnet}
                  className="flex flex-col items-center gap-1 py-3 rounded-lg border border-neutral-800 hover:border-cyan-500/50 hover:bg-neutral-900 text-neutral-300 hover:text-cyan-300 transition-colors"
                >
                  <Copy size={16} />
                  <span className="text-[10px] font-mono uppercase tracking-wider">Copy</span>
                </button>
                <a
                  data-testid="details-open-magnet"
                  href={merged.magnetUri}
                  className="flex flex-col items-center gap-1 py-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-bold transition-colors"
                >
                  <Magnet size={16} />
                  <span className="text-[10px] font-mono uppercase tracking-wider">Magnet</span>
                </a>
                <a
                  data-testid="details-download-torrent"
                  href={torrentFileUrl({
                    infoHash: t.infoHash,
                    link: merged.fileDownloadLink,
                    name: merged.name,
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col items-center gap-1 py-3 rounded-lg border border-neutral-800 hover:border-cyan-500/50 hover:bg-neutral-900 text-neutral-300 hover:text-cyan-300 transition-colors"
                >
                  <Download size={16} />
                  <span className="text-[10px] font-mono uppercase tracking-wider">.torrent</span>
                </a>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg border border-neutral-800 bg-neutral-900/40">
                <Stat icon={HardDrive} label="Size" value={merged.size} className="text-neutral-200" />
                <Stat icon={Calendar} label="Uploaded" value={relativeDate(merged.uploadDate)} className="text-neutral-400" />
                <Stat icon={Users} label="Seeders" value={merged.seeders} className="text-emerald-400 font-bold" />
                <Stat icon={Activity} label="Peers" value={merged.peers} className="text-rose-400" />
                <Stat icon={Tag} label="Category" value={merged.category} className="text-neutral-300" />
                {merged.uploader && (
                  <Stat icon={User} label="Uploader" value={merged.uploader} className="text-neutral-300" />
                )}
              </div>

              {/* Info hash */}
              <div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Info Hash</span>
                <p className="font-mono text-[11px] text-neutral-400 break-all mt-1 select-all">{t.infoHash}</p>
              </div>

              {/* Loading state */}
              {loading && (
                <div className="font-mono text-cyan-400 text-xs blink-cursor">$ fetching details</div>
              )}

              {/* Screenshots */}
              {merged.screenshots.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">
                    Screenshots
                  </h3>
                  <div className="grid grid-cols-1 gap-2" data-testid="details-screenshots">
                    {merged.screenshots.map((s, i) => (
                      <img
                        key={i}
                        src={s}
                        alt={`screenshot ${i + 1}`}
                        loading="lazy"
                        className="rounded-lg border border-neutral-800 w-full"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {merged.description ? (
                <div>
                  <h3 className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">
                    Description
                  </h3>
                  <div
                    data-testid="details-description"
                    className="text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap break-words [&_a]:text-cyan-400 [&_a]:break-all [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2"
                    dangerouslySetInnerHTML={{ __html: merged.description }}
                  />
                </div>
              ) : (
                !loading &&
                hasDetails && (
                  <p className="text-xs text-neutral-600 font-mono flex items-center gap-2">
                    <ImageOff size={13} /> No extended description available.
                  </p>
                )
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
